import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { Params } from '@angular/router';
import { inject } from '@angular/core';

import { injectQuery, injectQueryClient } from '@ngneat/query';
import { TranslateService } from '@ngx-translate/core';
import { LanguageCode } from '@senet-uiverse/i18n';
import { map, tap } from 'rxjs';

import { addListEntity, downloadBlobFile, removeListEntity, updateListEntity } from '../../helpers';
import { IPageData } from '../../types';

import { getDocumentTranslation, updateQueryDocumentFile } from './helpers';
import {
  IDocumentSignatureStatus,
  ILatestDocumentsParams,
  ISignatureStatusParams,
  I18N_DOCUMENT_TYPE_MAP,
  IDocumentTranslation,
  EDocumentPublishMode,
  IDocumentSignature,
  ISignaturesParams,
  IDocumentForSign,
  IDocumentsParams,
  IDocumentVersion,
  ISignedDocument,
  ISignParams,
} from './types';

export abstract class DocumentsApiService {
  private readonly _endpoints = {
    archive: (documentId: string) => `/api/v1/documents/${documentId}/archive`,
    publish: (documentId: string) => `/api/v1/documents/${documentId}/publish`,
    upload: (documentId: string) => `/api/v1/documents/${documentId}/upload`,
    sign: (documentId: string) => `/api/v1/documents/${documentId}/sign`,
    delete: (documentId: string) => `/api/v1/documents/${documentId}`,
    publicSignaturesStatus: '/api/v1/documents/public/signatures/status',
    downloadSignatures: '/api/v1/documents/signatures/export',
    signaturesStatus: '/api/v1/documents/signatures/status',
    publicLatest: '/api/v1/documents/public/latest',
    signatures: '/api/v1/documents/signatures',
    venues: '/api/v1/documents/organizations',
    latest: '/api/v1/documents/latest',
    versions: '/api/v1/documents',
    create: '/api/v1/documents',
  };

  abstract readonly endpointsConfig: { endingSlash?: boolean; urlFn?: (endpoint: string) => string };
  abstract readonly queryKey?: Array<string | number>;
  abstract readonly orgLanguage: LanguageCode;

  protected readonly translateService = inject(TranslateService);
  protected readonly queryClient = injectQueryClient();
  protected readonly httpClient = inject(HttpClient);
  protected readonly useQuery = injectQuery();
  protected readonly endpoints = new Proxy(this._endpoints, {
    get: (target, prop: keyof typeof this._endpoints) => {
      const processUrl = (url: string) => {
        let result = url;

        if (this.endpointsConfig.urlFn) {
          result = this.endpointsConfig.urlFn(url);
        }

        return this.endpointsConfig.endingSlash ? `${result}/` : result;
      };

      const endpoint = target[prop];

      if (typeof endpoint === 'function') {
        return (documentId: string) => {
          const url = endpoint(documentId);

          return processUrl(url);
        };
      }

      return processUrl(endpoint);
    },
  });

  createVersion(data: Partial<IDocumentVersion>, queryKey = this.queryKey) {
    return this.httpClient.post<IDocumentVersion>(this.endpoints.create, data).pipe(
      tap((response) => {
        if (queryKey) {
          this.queryClient.setQueryData(queryKey, addListEntity(response));
        }
      }),
    );
  }

  archiveVersion(documentId: string, queryKey = this.queryKey) {
    return this.httpClient.post<void>(this.endpoints.archive(documentId), {}).pipe(
      tap(() => {
        if (queryKey) {
          this.queryClient.setQueryData(queryKey, removeListEntity(documentId));
        }
      }),
    );
  }

  publishVersion(data: { documentId: string; publishMode: EDocumentPublishMode }, queryKey = this.queryKey) {
    return this.httpClient
      .post<IDocumentVersion>(this.endpoints.publish(data.documentId), {
        publish_mode: data.publishMode,
      })
      .pipe(
        tap((response) => {
          if (queryKey) {
            this.queryClient.setQueryData(queryKey, updateListEntity(data.documentId, response));
          }
        }),
      );
  }

  deleteVersion(documentId: string, queryKey = this.queryKey) {
    return this.httpClient.delete<void>(this.endpoints.delete(documentId)).pipe(
      tap(() => {
        if (queryKey) {
          this.queryClient.setQueryData(queryKey, removeListEntity(documentId));
        }
      }),
    );
  }

  uploadFile(data: { documentId: string; language: LanguageCode; file: File }, queryKey = this.queryKey) {
    const formData = new FormData();

    formData.append('language', data.language);
    formData.append('file', data.file);

    return this.httpClient
      .post<IDocumentTranslation>(this.endpoints.upload(data.documentId), formData, {
        reportProgress: true,
        observe: 'events' as const,
      })
      .pipe(
        tap((event: HttpEvent<IDocumentTranslation>) => {
          if (event.type === HttpEventType.Response && event.body && queryKey) {
            this.queryClient.setQueryData(queryKey, updateQueryDocumentFile(data.documentId, event.body));
          }
        }),
      );
  }

  getLatestQuery(params: ILatestDocumentsParams = {}, queryKey = this.queryKey || []) {
    return this.useQuery<IDocumentVersion[], HttpErrorResponse>({
      queryKey: queryKey,
      queryFn: () => this.getLatest(params).pipe(map((response) => response.data)),
    });
  }

  getLatest(params: ILatestDocumentsParams) {
    return this.httpClient.get<IPageData<IDocumentVersion>>(this.endpoints.latest, {
      params: params as Params,
    });
  }

  getPublicLatest(params: ILatestDocumentsParams) {
    return this.httpClient.get<IPageData<IDocumentVersion>>(this.endpoints.publicLatest, {
      params: params as Params,
    });
  }

  getDocuments(params: IDocumentsParams) {
    if (!params.limit) {
      params.limit = 999;
    }

    return this.httpClient.get<IPageData<IDocumentVersion>>(this.endpoints.versions, {
      params: params as Params,
    });
  }

  getSignatures(params: ISignaturesParams) {
    return this.httpClient.get<IPageData<IDocumentSignature>>(this.endpoints.signatures, {
      params: params as Params,
    });
  }

  downloadSignaturesReport(params: ISignaturesParams, hasAcceptHeader = false) {
    return this.httpClient
      .get(this.endpoints.downloadSignatures, {
        ...(hasAcceptHeader && { headers: { Accept: 'text/csv' } }),
        params: params as Params,
        responseType: 'blob',
      })
      .pipe(tap((blob) => downloadBlobFile(blob, 'signatures_report.csv')));
  }

  getSignaturesStatus(params: ISignatureStatusParams) {
    return this.httpClient.post<IPageData<IDocumentSignatureStatus>>(this.endpoints.signaturesStatus, params);
  }

  getPublicSignaturesStatus(params: ISignatureStatusParams) {
    return this.httpClient.post<IPageData<IDocumentSignatureStatus>>(this.endpoints.publicSignaturesStatus, params);
  }

  getDocumentsForSign(params: ISignatureStatusParams, requiredOnly = false) {
    return this.getSignaturesStatus(params).pipe(
      map((response) => {
        return response.data
          .filter((document) => {
            return requiredOnly ? document.requireSignature : true;
          })
          .map((item) => this.mapDocumentForSign(item.document));
      }),
    );
  }

  signDocument(params: ISignParams) {
    const { documentId, ...data } = params;

    return this.httpClient.post<ISignedDocument>(this.endpoints.sign(params.documentId), data);
  }

  mapDocumentForSign(
    document: IDocumentVersion,
    currentLang: LanguageCode = this.translateService.currentLang as LanguageCode,
    orgLanguage: LanguageCode = this.orgLanguage || LanguageCode.en,
  ) {
    const name = I18N_DOCUMENT_TYPE_MAP.get(document.documentType) || document.documentType;

    return <IDocumentForSign>{
      translation: getDocumentTranslation(document, currentLang, orgLanguage),
      documentName: this.translateService.instant(name),
      document: document,
    };
  }
}
